import { type ReactNode, type RefObject, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface OverlayProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  contentRef?: RefObject<HTMLDivElement | null>;
  triggerRef?: RefObject<HTMLElement | null>;
  initialFocusRef?: RefObject<HTMLElement | null>;
  autoFocus?: boolean;
  trapFocus?: boolean;
  dismissOnEscape?: boolean;
  dismissOnOutsideClick?: boolean;
  lockScroll?: boolean;
  container?: Element;
  className?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const openStack: string[] = [];

let scrollLockCount = 0;
let savedOverflow = '';
let savedPaddingRight = '';

function lockBodyScroll() {
  if (scrollLockCount === 0) {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    savedOverflow = document.body.style.overflow;
    savedPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      const basePadding = Number.parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${basePadding + scrollbarWidth}px`;
    }
  }
  scrollLockCount += 1;
}

function unlockBodyScroll() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    document.body.style.overflow = savedOverflow;
    document.body.style.paddingRight = savedPaddingRight;
  }
}

function focusables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.getClientRects().length > 0,
  );
}

export function Overlay({
  open,
  onClose,
  children,
  contentRef,
  triggerRef,
  initialFocusRef,
  autoFocus = true,
  trapFocus = true,
  dismissOnEscape = true,
  dismissOnOutsideClick = true,
  lockScroll = true,
  container,
  className,
}: OverlayProps) {
  const id = useId();
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open || !lockScroll) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [open, lockScroll]);

  useEffect(() => {
    if (!open) return;

    openStack.push(id);
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (autoFocus) {
      const target = initialFocusRef?.current ?? (surfaceRef.current ? focusables(surfaceRef.current)[0] : undefined);
      (target ?? surfaceRef.current)?.focus();
    }

    return () => {
      const index = openStack.indexOf(id);
      if (index !== -1) openStack.splice(index, 1);
      const active = document.activeElement;
      const stillInside = active == null || active === document.body || surfaceRef.current?.contains(active);
      if (stillInside) (triggerRef?.current ?? previousFocusRef.current)?.focus();
    };
  }, [open, autoFocus, initialFocusRef, triggerRef, id]);

  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (openStack[openStack.length - 1] !== id) return;

      if (event.key === 'Escape' && dismissOnEscape) {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key === 'Tab' && trapFocus && surfaceRef.current) {
        const nodes = focusables(surfaceRef.current);
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (!first || !last) {
          event.preventDefault();
          return;
        }
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, id, dismissOnEscape, trapFocus, onClose]);

  useEffect(() => {
    if (!open || !dismissOnOutsideClick) return;

    function handlePointerDown(event: PointerEvent) {
      if (openStack[openStack.length - 1] !== id) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (surfaceRef.current?.contains(target)) return;
      if (triggerRef?.current?.contains(target)) return;
      onClose();
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open, id, dismissOnOutsideClick, triggerRef, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={(node) => {
        surfaceRef.current = node;
        if (contentRef) contentRef.current = node;
      }}
      className={className}
    >
      {children}
    </div>,
    container ?? document.body,
  );
}
